import { equals } from "../../utils/mathUtils";
import {
  ChangeSceneResponseI,
  DataSelectedObjectI,
  DataSelectSimpleI,
  LoadAssetResponseI,
  TransformDataAssetI,
} from "./type";
import ImageService from "../ImageService";
import { UrlService } from "../UrlService";

class PlayCanvasService {
  private rootManager: any;
  private assetManager: any;

  public initHandlers: boolean = false;

  private static instance: PlayCanvasService;
  public static getInstance(): PlayCanvasService {
    if (!PlayCanvasService.instance) {
      PlayCanvasService.instance = new PlayCanvasService();
    }
    return PlayCanvasService.instance;
  }

  public static getIframeWindow(): any | null {
    //@ts-ignore
    const containerRef = window.containerRef;
    if (!containerRef || !containerRef.current) return null;
    return containerRef.current.contentWindow;
  }

  public loadRootManager(): void {
    const iframeWindow = PlayCanvasService.getIframeWindow();
    if (iframeWindow?.Vivid) {
      this.rootManager = iframeWindow.Vivid;
    }
  }

  public clearRootManager(): void {
    this.rootManager = false;
  }

  public async loadAssetManager(): Promise<void> {
    const iframeWindow = PlayCanvasService.getIframeWindow();
    if (iframeWindow) {
      this.assetManager = iframeWindow.vividAPI;
    }
  }

  public async changeScene(sceneId: string): Promise<ChangeSceneResponseI> {
    debugger
    await this.waitForRootManager();
    const res = await this.rootManager?.changeScene(sceneId);
    await this.loadAssetManager();

    return res;
  }

  public async loadAssetById(
    id: number | string,
    url: string
  ): Promise<LoadAssetResponseI> {
    const cdnUrl = UrlService.getGlbCdnUrl(url);
    return await this.assetManager?.loadAsset(id, cdnUrl);
  }

  public async removeAssetById(id: number | string): Promise<void> {
    await this.assetManager?.removeAsset(id);
  }

  public async replaceAssetById(
    id: number | string,
    url: string
  ): Promise<LoadAssetResponseI> {
    const cdnUrl = UrlService.getGlbCdnUrl(url);
    return await this.assetManager?.replaceModel(id, cdnUrl);
  }

  public async clearScene(): Promise<void> {
    await this.assetManager?.clearScene();
  }

  public async loadAssetWithParams(
    id: number | string,
    url: string,
    transform: TransformDataAssetI
  ): Promise<LoadAssetResponseI> {

    const cdnUrl = UrlService.getGlbCdnUrl(url);

    return await this.assetManager?.loadAssetWithTransform(id, cdnUrl, transform);
  }

  public async getTransformDataAssetById(
    id: number | string
  ): Promise<TransformDataAssetI> {
    const transform = await this.assetManager?.getObjectTransform(id);
    return {
      position: {
        x: transform.position.x,
        y: transform.position.y,
        z: transform.position.z,
      },
      rotation: {
        x: transform.rotation.x,
        y: transform.rotation.y,
        z: transform.rotation.z,
      },
      scale: {
        x: transform.scale.x,
        y: transform.scale.y,
        z: transform.scale.z,
      },
    };
  }

  public addListenerMouseDown(callback: () => void): void {
    this.assetManager?.handleMouseDown(callback);
  }

  public addListenerMouseUp(callback: () => void): void {
    this.assetManager.handleMouseUp(callback);
  }

  public onSelectedSimple(callback: (data: DataSelectSimpleI) => void): void {
    this.assetManager.onSelectedSimple(callback);
  }

  public getSelectedObject(): DataSelectedObjectI | null {
    return this.assetManager.getSelectedObject();
  }

  public comparedTransformAssetById(
    transform1: TransformDataAssetI,
    transform2: TransformDataAssetI
  ): boolean {
    return (
      equals(transform1.position.x, transform2.position.x) &&
      equals(transform1.position.y, transform2.position.y) &&
      equals(transform1.position.z, transform2.position.z) &&
      equals(transform1.rotation.x, transform2.rotation.x) &&
      equals(transform1.rotation.y, transform2.rotation.y) &&
      equals(transform1.rotation.z, transform2.rotation.z) &&
      equals(transform1.scale.x, transform2.scale.x) &&
      equals(transform1.scale.y, transform2.scale.y) &&
      equals(transform1.scale.z, transform2.scale.z)
    );
  }

  private async waitForRootManager(
    timeout = 8000,
    interval = 100
  ): Promise<void> {
    const maxAttempts = Math.ceil(timeout / interval);
    let attempts = 0;
    while (!this.rootManager && attempts < maxAttempts) {
      this.loadRootManager();
      if (this.rootManager && this.rootManager.isLoading) {
        break;
      }
      await new Promise(resolve => setTimeout(resolve, interval));
      attempts++;
    }
    if (!this.rootManager) {
      throw new Error("rootManager not available");
    }
  }

  public captureScreenshot(): string | null {
    const iframeWin = PlayCanvasService.getIframeWindow();
    if (!iframeWin) return null;
    const canvas = iframeWin.document.getElementById(
      "application-canvas"
    ) as HTMLCanvasElement | null;
    if (!canvas) return null;

    // Check actual WebGL context preserveDrawingBuffer before capture
    const actualBufferSetting = this.checkWebGLPreserveDrawingBuffer();

    if (actualBufferSetting === false) {
      console.warn(
        "⚠️ WebGL context has preserveDrawingBuffer: false - screenshot may be black!"
      );
    }

    return canvas.toDataURL("image/png");
  }

  //  Captures a screenshot and returns it as a File object

  public captureScreenshotFile(
    filename: string = "screenshot.png"
  ): File | null {
    const dataUrl = this.captureScreenshot();
    if (!dataUrl) {
      return null;
    }
    return ImageService.dataUrlToFile(dataUrl, filename);
  }

  /**
   * Get PlayCanvas context options from iframe
   */
  public getContextOptions(): any | null {
    const iframeWin = PlayCanvasService.getIframeWindow();
    return iframeWin?.CONTEXT_OPTIONS || null;
  }

  /**
   * Check if WebGL context actually has preserveDrawingBuffer enabled
   */
  public checkWebGLPreserveDrawingBuffer(): boolean | null {
    const iframeWin = PlayCanvasService.getIframeWindow();
    if (!iframeWin) return null;

    const canvas = iframeWin.document.getElementById(
      "application-canvas"
    ) as HTMLCanvasElement;
    if (!canvas) return null;

    const gl = canvas.getContext("webgl") || canvas.getContext("webgl2");
    if (!gl) return null;

    // Check the actual WebGL context parameter
    const preserveDrawingBuffer =
      gl.getContextAttributes()?.preserveDrawingBuffer;

    return preserveDrawingBuffer || false;
  }

  /**
   * Override PlayCanvas context options before initialization
   */
  public initializePlayCanvasOverrides(): Promise<void> {
    return new Promise(resolve => {
      const checkAndOverride = () => {
        const iframeWin = PlayCanvasService.getIframeWindow();

        // Early override - as soon as iframe window is available
        if (iframeWin) {
          // Intercept HTMLCanvasElement.getContext to force preserveDrawingBuffer
          if (iframeWin.HTMLCanvasElement && !iframeWin._webglIntercepted) {
            const originalGetContext =
              iframeWin.HTMLCanvasElement.prototype.getContext;

            iframeWin.HTMLCanvasElement.prototype.getContext = function (
              contextType: string,
              contextAttributes?: any
            ) {
              if (contextType === "webgl" || contextType === "webgl2") {
                // Force preserveDrawingBuffer to true
                const modifiedAttributes = {
                  ...contextAttributes,
                  preserveDrawingBuffer: true,
                };

                return originalGetContext.call(
                  this,
                  contextType,
                  modifiedAttributes
                );
              }
              return originalGetContext.call(
                this,
                contextType,
                contextAttributes
              );
            };

            iframeWin._webglIntercepted = true;
          }

          // Also override config files
          if (iframeWin.pc?.config?.application_properties) {
            iframeWin.pc.config.application_properties.preserveDrawingBuffer = true;
          }

          resolve();
        } else {
          // Retry if iframe not ready yet
          setTimeout(checkAndOverride, 50);
        }
      };

      checkAndOverride();
    });
  }

  /**
   * Instance wrapper for static iframe access
   */
  public getIframeWindow(): any | null {
    return PlayCanvasService.getIframeWindow();
  }
}

export default PlayCanvasService.getInstance();
